import { BaseModel } from "@models/base.model"
import { makeObservable, observable } from "mobx"
import { Localization } from "@serializers/localization"
import { CareersValue } from "@serializers/careers-value"
import { CareersBenefit } from "@serializers/careers-benefit"
import { CareersPerson } from "@serializers/careers-person"
import { htmlToTextTransform } from "@lib/html"

export class VacanciesModel extends BaseModel {
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  // Copy from the older VacanciesPage single type, which holds every language in
  // one entry. Kept as a fallback for keys not yet moved across.
  @observable templates: Localization[] = []

  // Copy from the localized CareersPage single type, already in the current
  // language. This is where the careers page copy lives now.
  @observable careersTemplates: Localization[] = []

  // The three repeating lists, from the localized CareersPage single type. Each
  // is already in the current language, and every item carries the key of the
  // slot it fills, so the page finds its own items rather than reading the list
  // in order.
  @observable values: CareersValue[] = []
  @observable benefits: CareersBenefit[] = []
  @observable people: CareersPerson[] = []

  getVacanciesPage = async () => {
    // Both endpoints are fetched together, so the second one does not add a
    // round trip to the page's server render, and each is caught on its own so
    // one failing does not empty the other.
    const [older, newer] = await Promise.all([
      this.Providers.VacanciesProvider.getCareersTemplates(
        this.Models.GlobalModel.lang,
      ).catch(() => null),
      this.Providers.VacanciesProvider.getCareersPage(
        this.Models.GlobalModel.lang,
      ).catch(() => null),
    ])

    this.applyData({
      templates: older?.templates ?? [],
      careersTemplates: newer?.careersTemplates ?? [],
      values: newer?.values ?? [],
      benefits: newer?.benefits ?? [],
      people: newer?.people ?? [],
    })
  }

  // Looks in CareersPage first, then falls back to the older VacanciesPage
  // templates. That lets copy move one key at a time: a key present on
  // CareersPage wins, and anything not moved yet still resolves.
  getTemplate = (key: string, simpleText = true) => {
    const find =
      this.careersTemplates.find((template) => template.key === key) ??
      this.templates.find((template) => template.key === key)
    if (!find) return ""

    return simpleText ? htmlToTextTransform(find.template) : find.template
  }

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({
      // @ts-ignore
      templates: data?.templates ?? [],
      // @ts-ignore
      careersTemplates: data?.careersTemplates ?? [],
      // Default to empty, so a payload serialized before these fields existed
      // still hydrates instead of leaving the observables undefined.
      // @ts-ignore
      values: data?.values ?? [],
      // @ts-ignore
      benefits: data?.benefits ?? [],
      // @ts-ignore
      people: data?.people ?? [],
    })
  }

  serialize = () => {
    return {
      templates: this.templates,
      careersTemplates: this.careersTemplates,
      values: this.values,
      benefits: this.benefits,
      people: this.people,
    }
  }
}
